/**
 * Formatação de data e hora.
 *
 * REGRA DO PROJETO (architecture.md §5):
 *  · a persistência é sempre `TIMESTAMPTZ` em UTC;
 *  · a EXIBIÇÃO é sempre no fuso da ORGANIZAÇÃO (`organization.timeZone`),
 *    nunca no fuso do browser — dois usuários da mesma empresa em estados
 *    diferentes precisam ler o mesmo horário no mesmo registro;
 *  · quando o dado é REGULADO (assinatura, decisão de aprovação, evento de
 *    trilha), o fuso vai VISÍVEL junto do horário. Um carimbo de tempo sem fuso
 *    é ambíguo, e ambiguidade em registro assinado é achado de inspeção;
 *  · datas PURAS (`revisionDate`, `validUntil`) são `DATE` no banco e não têm
 *    fuso — são formatadas sem conversão nenhuma.
 */

export const FALLBACK_TIME_ZONE =
  (import.meta.env.VITE_FALLBACK_TIME_ZONE as string | undefined) ?? 'America/Sao_Paulo';

type DateLike = Date | string | number | null | undefined;

function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
  formatterCache.set(key, formatter);
  return formatter;
}

/**
 * Data pura `AAAA-MM-DD` → `dd/mm/aaaa`.
 * NÃO passa por `Date`/fuso: `new Date('2026-03-14')` seria interpretado como
 * meia-noite UTC e, em UTC-3, exibiria 13/03. Este é exatamente o bug que a
 * conversão ingênua produz — por isso o parse é textual.
 */
export function formatDateOnly(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return '—';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/** `dd/mm/aaaa` no fuso da organização. Para instantes (TIMESTAMPTZ). */
export function formatDate(
  value: DateLike,
  timeZone: string,
  locale = 'pt-BR',
): string {
  const date = toDate(value);
  if (!date) return '—';
  return getFormatter(locale, timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** `dd/mm/aaaa hh:mm` no fuso da organização, SEM o rótulo do fuso. */
export function formatDateTime(
  value: DateLike,
  timeZone: string,
  locale = 'pt-BR',
): string {
  const date = toDate(value);
  if (!date) return '—';
  return getFormatter(locale, timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * `dd/mm/aaaa hh:mm:ss (UTC-03)` — formato do manifesto de assinatura (§5.4 do
 * plano). Use SEMPRE que o dado for regulado: assinatura, decisão, evento de
 * trilha, ciência.
 */
export function formatRegulatedDateTime(
  value: DateLike,
  timeZone: string,
  locale = 'pt-BR',
): string {
  const date = toDate(value);
  if (!date) return '—';

  // Montado a partir das PARTES, não do `format()` direto: em pt-BR o `Intl`
  // insere vírgula entre data e hora ("14/03/2026, 09:42:17") e o formato do
  // manifesto de assinatura não tem vírgula. Depender de `replace(',', '')`
  // amarraria o carimbo regulado ao separador de UM locale.
  const parts = getFormatter(locale, timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const parte = (tipo: string) => parts.find((p) => p.type === tipo)?.value ?? '';
  const base =
    `${parte('day')}/${parte('month')}/${parte('year')} ` +
    `${parte('hour')}:${parte('minute')}:${parte('second')}`;

  return `${base} (${timeZoneAbbreviation(date, timeZone, locale)})`;
}

/**
 * O fuso é utilizável para formatar?
 *
 * `new Intl.DateTimeFormat(locale, { timeZone })` LEVANTA `RangeError` para
 * fuso vazio ou desconhecido — e num componente React isso sobe até o
 * ErrorBoundary e derruba a TELA INTEIRA. Foi o que aconteceu na página de
 * organização: o `Controller` do formulário devolve string vazia para o campo
 * que ainda não recebeu valor, e o relógio "Agora são HH:MM" formatava essa
 * string vazia no primeiro quadro depois de a consulta responder.
 */
export function isUsableTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('pt-BR', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Hora do dia num fuso, ou `null` quando o fuso ainda não é utilizável.
 *
 * Devolver `null` em vez de cair para o fuso do navegador é deliberado: num
 * sistema regulado, hora exibida com fuso errado é pior do que hora ausente —
 * o leitor não teria como saber que está olhando outro relógio. Quem chama
 * mostra um travessão e segue.
 */
export function formatTimeOfDay(
  date: Date,
  timeZone: string | null | undefined,
  locale = 'pt-BR',
): string | null {
  if (!isUsableTimeZone(timeZone)) return null;
  return getFormatter(locale, timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * Rótulo curto do fuso, no formato `UTC-03`.
 * `shortOffset` é o único que produz um deslocamento inequívoco; `short`
 * devolveria "BRT", que muda de significado com o horário de verão.
 */
export function timeZoneAbbreviation(
  date: Date,
  timeZone: string,
  locale = 'pt-BR',
): string {
  try {
    const parts = getFormatter(locale, timeZone, { timeZoneName: 'shortOffset' }).formatToParts(
      date,
    );
    const name = parts.find((part) => part.type === 'timeZoneName')?.value;
    if (!name) return timeZone;
    // Intl devolve "GMT-3"; normalizamos para "UTC-03", que é o formato usado
    // no manifesto impresso e no PDF/A.
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name);
    // Em UTC o `Intl` devolve só "GMT", sem deslocamento. Deixar "UTC" seria
    // ambíguo justamente onde o formato existe para não ser: o leitor não sabe
    // se o zero foi aplicado ou se a informação faltou. "UTC+00" afirma.
    if (!match) return name === 'GMT' || name === 'UTC' ? 'UTC+00' : name.replace('GMT', 'UTC');
    const [, sign, hours, minutes] = match;
    const paddedHours = String(hours).padStart(2, '0');
    return minutes && minutes !== '00'
      ? `UTC${sign}${paddedHours}:${minutes}`
      : `UTC${sign}${paddedHours}`;
  } catch {
    return timeZone;
  }
}

/** Nome do fuso por extenso, para a tela de configuração. */
export function timeZoneLongName(timeZone: string, locale = 'pt-BR'): string {
  try {
    const parts = getFormatter(locale, timeZone, { timeZoneName: 'long' }).formatToParts(
      new Date(),
    );
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/**
 * Tempo relativo em português ("há 3 dias", "em 2 horas").
 *
 * Complementa — nunca substitui — a data absoluta. Em registro regulado o
 * carimbo exato é obrigatório; o relativo é conforto de leitura.
 */
export function formatRelative(value: DateLike, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return '—';

  const diffMs = date.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

  const MINUTE = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const MONTH = 2_592_000_000;

  if (abs < MINUTE) return 'agora mesmo';
  if (abs < HOUR) return rtf.format(Math.round(diffMs / MINUTE), 'minute');
  if (abs < DAY) return rtf.format(Math.round(diffMs / HOUR), 'hour');
  if (abs < MONTH) return rtf.format(Math.round(diffMs / DAY), 'day');
  if (abs < MONTH * 12) return rtf.format(Math.round(diffMs / MONTH), 'month');
  return rtf.format(Math.round(diffMs / (MONTH * 12)), 'year');
}

/** Duração `m:ss` — usada na contagem regressiva da prova de assinatura. */
export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Hash SHA-256 truncado no formato do manifesto: `a3f8…9c21`. */
export function truncateHash(hash: string, head = 4, tail = 4): string {
  if (hash.length <= head + tail) return hash;
  return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
}

/** Número no padrão brasileiro. */
export function formatNumber(value: number, locale = 'pt-BR'): string {
  return new Intl.NumberFormat(locale).format(value);
}
